import { useMemo, useState } from 'react'
import { api, qs } from '../api.js'
import {
  Banner, Button, Card, ErrorState, Field, Loading, SectionHeader, Table,
} from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * Report downloads, native to the console.
 *
 * **Why this page exists.** Six of the seven legacy admin tools were ported to the console; reports was the one
 * left behind, still linked out to the static `/admin/reports.html`. That page carries its own login form and
 * its own in-memory token, so **every** download asked for credentials again — reported as "in the reports
 * section, why is every report asking for login?" The comment I left on `Admin.jsx` claimed the link was
 * deliberate because the static page "already handles the token"; that reasoning was wrong. `api()` has had a
 * `raw: true` mode all along, with the single-flight refresh already in it.
 *
 * A fetch-and-blob rather than an `<a href>`: the endpoint needs the bearer token, and an anchor sends an
 * unauthenticated request, so the browser would save the 401 body under a `.xlsx` name. That is the exact
 * "an error page under a document name" failure the M9-01 audit fixed on the Android side.
 *
 * **Not ADMIN-gated.** `ReportController` admits ADMIN, MANAGEMENT, SAFETY_OFFICER, ELECTRICAL and MECHANICAL —
 * a plant head exporting the compliance register is the read-only case MANAGEMENT exists for. The two roles it
 * refuses, OPERATOR and VENDOR, cannot reach the console at all. So the page is open and the server stays the
 * authority.
 */
export default function Reports() {
  const catalogue = useApi('/reports')
  const assets = useApi('/assets')
  const departments = useApi('/departments')

  const [open, setOpen] = useState(null)      // the report being configured
  const [busy, setBusy] = useState(null)      // its key, while downloading
  const [notice, setNotice] = useState(null)

  const rows = useMemo(() => (Array.isArray(catalogue.data) ? catalogue.data : []), [catalogue.data])
  const fleet = useMemo(() => (Array.isArray(assets.data) ? assets.data : []), [assets.data])
  const depts = useMemo(() => (Array.isArray(departments.data) ? departments.data : []), [departments.data])

  async function download(report, filters, format) {
    setBusy(report.key)
    setNotice(null)
    try {
      // `params`, not `parameters`. The server's `ReportDefinitionResponse` calls it `params`, and reading the
      // other name is why the Filters column on the Setup page always said "None" — a shape guessed from
      // memory instead of from the endpoint, which is the mistake that also produced the Setup white screen.
      const query = qs({ ...filters, format })
      const res = await api(`/reports/${report.key}/download${query}`, { raw: true })
      const blob = await res.blob()

      // An XLSX is a ZIP, so it starts "PK". A JSON or HTML error body saved under a .xlsx name passes every
      // other check and fails in the recipient's spreadsheet reader, hours later and off this machine.
      if (format === 'xlsx') {
        const head = new Uint8Array(await blob.slice(0, 2).arrayBuffer())
        if (head[0] !== 0x50 || head[1] !== 0x4b) {
          setNotice({ kind: 'danger', text: 'The download is not a readable workbook. Try again.' })
          return
        }
      }

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${report.key}-${(filters.to || new Date().toISOString()).slice(0, 10)}.${format}`
      a.click()
      // Revoked on the next tick: some browsers cancel an in-flight download if the object URL disappears
      // before the click has been dispatched.
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      setNotice({ kind: 'success', text: `${report.title} downloaded.` })
      setOpen(null)
    } catch (e) {
      // The server's own words. "Check your connection" for a 403 sent a supervisor to restart their phone
      // over a permission they did not have (M9-01).
      setNotice({ kind: 'danger', text: e.message })
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Generated on the server as XLSX or CSV, for management review, audits and insurers."
      />

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {catalogue.loading && <Loading rows={6} />}
      {catalogue.error && <ErrorState error={catalogue.error} onRetry={catalogue.reload} />}

      {catalogue.data && (
        <>
          <SectionHeader hint="Click a report to choose its filters and format">Available reports</SectionHeader>
          <Card>
            <Table
              keyOf={(r) => r.key}
              onRowClick={(r) => setOpen(r)}
              columns={[
                { key: 'title', header: 'Report', render: (r) => <strong>{r.title}</strong> },
                { key: 'description', header: 'What it contains' },
                {
                  key: 'params',
                  header: 'Filters',
                  render: (r) => (r.params?.length
                    ? r.params.map(labelFor).join(' · ')
                    : <span className="muted">None</span>),
                },
                {
                  key: 'go',
                  header: '',
                  render: (r) => (
                    <Button
                      variant="secondary"
                      disabled={busy === r.key}
                      onClick={(e) => { e.stopPropagation(); setOpen(r) }}
                    >
                      {busy === r.key ? 'Preparing…' : 'Download'}
                    </Button>
                  ),
                },
              ]}
              rows={rows}
              empty={<p className="muted">No reports available.</p>}
            />
          </Card>
        </>
      )}

      {open && (
        <FilterSheet
          report={open}
          fleet={fleet}
          depts={depts}
          busy={busy === open.key}
          onClose={() => setOpen(null)}
          onDownload={(filters, format) => download(open, filters, format)}
        />
      )}
    </>
  )
}

/* ---- Filters + format --------------------------------------------------- */

function FilterSheet({ report, fleet, depts, busy, onClose, onDownload }) {
  const needs = (p) => report.params?.includes(p)
  // Last 90 days, matching the server's own default window, so the form agrees with what an unfiltered
  // download would have produced.
  const today = new Date()
  const ninetyAgo = new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000)
  const [f, setF] = useState({
    from: needs('from') ? ninetyAgo.toISOString().slice(0, 10) : '',
    to: needs('to') ? today.toISOString().slice(0, 10) : '',
    department: '',
    assetId: '',
  })
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  // A per-crane report without a crane would return the whole fleet or nothing useful, so it is required here
  // rather than discovered as an empty file.
  const canSubmit = !needs('assetId') || !!f.assetId

  function submit(format) {
    const filters = {}
    // Start-of-day / end-of-day in the browser's zone rather than UTC midnight, which lands on the previous
    // day for a plant at +05:30 and silently drops a shift from the window.
    if (needs('from') && f.from) filters.from = new Date(`${f.from}T00:00:00`).toISOString()
    if (needs('to') && f.to) filters.to = new Date(`${f.to}T23:59:59`).toISOString()
    if (needs('department') && f.department) filters.department = f.department
    if (needs('assetId') && f.assetId) filters.assetId = f.assetId
    onDownload(filters, format)
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <header className="sheet-head">
          <h2>{report.title}</h2>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div className="sheet-body">
          <p className="muted">{report.description}</p>

          {!report.params?.length && (
            <Banner kind="info">This report takes no filters — it covers the whole fleet.</Banner>
          )}

          <div className="grid-2">
            {needs('from') && (
              <Field label="From" id="rf" hint="Start of the window">
                <input id="rf" type="date" value={f.from} onChange={set('from')} />
              </Field>
            )}
            {needs('to') && (
              <Field label="To" id="rt" hint="End of the window">
                <input id="rt" type="date" value={f.to} onChange={set('to')} />
              </Field>
            )}
            {needs('department') && (
              <Field label="Department" id="rd" hint="Leave blank for the whole plant">
                <select id="rd" value={f.department} onChange={set('department')}>
                  <option value="">All departments</option>
                  {depts.map((d) => <option key={d} value={d}>{d}</option>)}
                </select>
              </Field>
            )}
            {needs('assetId') && (
              <Field label="Crane" id="ra" hint="This report covers one crane">
                <select id="ra" value={f.assetId} onChange={set('assetId')} required>
                  <option value="">Select…</option>
                  {fleet
                    .filter((a) => a.status !== 'DECOMMISSIONED')
                    .map((a) => <option key={a.id} value={a.id}>{a.assetCode} — {a.name}</option>)}
                </select>
              </Field>
            )}
          </div>
        </div>

        <footer className="sheet-foot">
          {/* Two buttons rather than a format dropdown plus a submit: the format IS the action here, and a
              dropdown would add a step to the most common thing anyone does on this page. */}
          <Button variant="secondary" disabled={busy || !canSubmit} onClick={() => submit('csv')}>
            {busy ? 'Preparing…' : 'Download CSV'}
          </Button>
          <Button disabled={busy || !canSubmit} onClick={() => submit('xlsx')}>
            {busy ? 'Preparing…' : 'Download Excel'}
          </Button>
        </footer>
      </div>
    </div>
  )
}

/** The server's parameter names are wire identifiers; these are what a person reads. */
const labelFor = (p) => ({
  from: 'From date',
  to: 'To date',
  department: 'Department',
  assetId: 'Crane',
}[p] || p)
